import { AnimatePresence } from "framer-motion";
import { useMemo, useCallback } from "react";

import { useCurrentMedia, useMediaStore } from "@/store";
import FilePreview from "@/components/file/Preview";
import { FileCarousel, FileCarouselLayout } from "@/components/file/Carousel/FileCarousel";
import { useMedia } from "@/contexts/MediaContext";
import { useCoreApp } from "@/contexts/CoreAppContext";
import { BoundingBoxStrategyImpl } from "@utils/annotationStrategies/BoundingBox";
import { FilePreviewProvider } from "@/contexts/FilePreviewProvider";
import { useTestWithMoreFiles } from "@/useTestWithMoreFiles";
import { VideoTrimmer } from "@/components/file/VideoTrimmer";
import { OutOfCreditsModal } from "@/components/flow/shared/OutOfCreditsModal";
import { MediaItemStatus } from "@/types/media";
import { getExtractedFrameIdsForMediaItem } from "@/hooks/useMediaUtils";
import { useRandomSourceLoader } from "@/hooks/useRandomSourceLoader";
import { NeedsEditingButton } from "./NeedsEditingButton";

const TestFilesVisualAreaContent = () => {
    // Load a random source from the dataset when there's no media
    const { isLoadingRandomSource } = useRandomSourceLoader();
    const { showLabels } = useCoreApp();
    const {
        testWithMoreFiles,
        uploadLimitError,
        clearUploadLimitError,
        isOutOfCredits,
        clearOutOfCreditsError
    } = useTestWithMoreFiles();
    const { mediaItemToTrim, pendingMediaItems } = useMedia();
    const currentMedia = useCurrentMedia();
    const media = useMediaStore();

    const onAddMediaItems = useCallback(
        async (files: File[], onFinally?: () => void) => {
            await testWithMoreFiles(files);

            onFinally?.();
        },
        [testWithMoreFiles]
    );

    // Check if current file is still uploading/processing
    const isCurrentFileProcessing = useMemo(() => {
        if (!currentMedia?.file) return false;

        const currentFile = currentMedia.file;

        // Check if it's in pending items
        if (pendingMediaItems.some((item) => item.id === currentFile.id)) {
            return true;
        }

        // Check if it's uploading or processing
        if (
            currentFile.status === MediaItemStatus.UPLOADING ||
            currentFile.status === MediaItemStatus.PROCESSING
        ) {
            return true;
        }

        // For videos, check if extracted frames are still uploading
        if (currentFile.isVideo) {
            const extractedFrameIds = getExtractedFrameIdsForMediaItem(currentFile);
            const uploadingFrames = extractedFrameIds.some(
                (frameId) => media[frameId]?.status === MediaItemStatus.UPLOADING
            );
            if (uploadingFrames) {
                return true;
            }
        }

        return false;
    }, [currentMedia?.file, pendingMediaItems, media]);

    return (
        <AnimatePresence mode="wait">
            <div className="visualArea relative flex h-full flex-col">
                <FilePreviewProvider>
                    <FileCarousel
                        className="h-full w-full"
                        onAddMediaItems={onAddMediaItems}
                        uploadLimitError={uploadLimitError}
                        clearUploadLimitError={clearUploadLimitError}
                        layout={FileCarouselLayout.MODEL}
                        isLoadingRandomSource={isLoadingRandomSource}
                    >
                        <FilePreview
                            key={`file-${currentMedia?.file.id}`}
                            forceHideLabels={!showLabels}
                            strategy={BoundingBoxStrategyImpl}
                            showProcessingOverlay={isCurrentFileProcessing}
                            processingOverlayText="Processing file..."
                        />
                        {/* Needs Editing button - floating over the canvas area */}
                        {!isCurrentFileProcessing && <NeedsEditingButton />}
                    </FileCarousel>
                </FilePreviewProvider>

                {/* Video Trimmer Modal */}
                {mediaItemToTrim?.validation?.needsTrimming === true && <VideoTrimmer />}

                {/* Out of Credits Modal */}
                <OutOfCreditsModal isOpen={isOutOfCredits} onClose={clearOutOfCreditsError} />
            </div>
        </AnimatePresence>
    );
};

export function DeployVisualAreaContent() {
    return <TestFilesVisualAreaContent />;
}
